// Schedule Management API Service
import { post, get, put, del } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { parseWaitlistError, waitlistedError } from '../utils/waitlistError';

// Helper to get current user ID
async function getUserId() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.payload?.sub || null;
  } catch (error) {
    console.error('Error getting user ID:', error);
    return null;
  }
}

// Helper to extract error message from Amplify API errors
async function extractErrorMessage(apiError, defaultMessage) {
  console.error('🔍 API Error caught:', apiError);
  console.error('🔍 API Error type:', apiError.name);
  console.error('🔍 API Error message:', apiError.message);
  
  // Check if there's a response body with more details
  if (apiError.response) {
    console.error('🔍 Error response:', apiError.response);
    try {
      let errorBody;
      if (apiError.response.body) {
        // If it's already an object (parsed)
        if (typeof apiError.response.body === 'object' && !(apiError.response.body instanceof ReadableStream)) {
          errorBody = apiError.response.body;
        } else if (typeof apiError.response.body === 'string') {
          errorBody = JSON.parse(apiError.response.body);
        } else {
          // It's a stream, try to read it
          const reader = apiError.response.body.getReader();
          const chunks = [];
          let done = false;
          while (!done) {
            const { value, done: streamDone } = await reader.read();
            if (value) chunks.push(value);
            done = streamDone;
          }
          const text = new TextDecoder().decode(new Uint8Array(chunks.flatMap(c => Array.from(c))));
          errorBody = JSON.parse(text);
        }
      }
      
      console.error('🔍 Error body:', JSON.stringify(errorBody, null, 2));
      // Plan-limit errors (schedule_run_limit / free_schedule_limit / custom_sources_requires_plus)
      // use { reason, message, limit } rather than { error, errors } — surface `message` verbatim
      // so users see the actual plan-aware explanation instead of a generic failure string.
      const errorMessage = errorBody.errors ?
        `Validation failed: ${JSON.stringify(errorBody.errors)}` :
        (errorBody.message || errorBody.error || apiError.message || defaultMessage);
      return errorMessage;
    } catch (parseError) {
      console.error('🔍 Could not parse error body:', parseError);
    }
  }
  
  return apiError.message || defaultMessage;
}

export const scheduleService = {
  /**
   * List all schedules for the authenticated user
   * @param {string} status - Optional status filter ('active', 'paused', 'deleted')
   * @returns {Promise<Array>} Array of schedules
   */
  async listSchedules(status = null) {
    try {
      
      const userId = await getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const path = status ? `/schedules?userId=${encodeURIComponent(userId)}&status=${status}` : `/schedules?userId=${encodeURIComponent(userId)}`;
      
      const result = await get({
        apiName: 'userdata',
        path: path
      }).response;
      
      const data = await result.body.json();
      
      if (data.success) {
        return data.schedules || [];
      } else {
        console.error('❌ Failed to fetch schedules:', data.error);
        throw new Error(data.error || 'Failed to fetch schedules');
      }
    } catch (error) {
      console.error('❌ Error fetching schedules:', error);
      throw error;
    }
  },

  /**
   * Get a specific schedule by ID
   * @param {string} scheduleId - Schedule ID
   * @returns {Promise<Object>} Schedule object
   */
  async getSchedule(scheduleId) {
    try {
      
      const userId = await getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const result = await get({
        apiName: 'userdata',
        path: `/schedules/${scheduleId}?userId=${encodeURIComponent(userId)}`
      }).response;
      
      const data = await result.body.json();
      
      if (data.success) {
        return data.schedule;
      } else {
        console.error('❌ Failed to fetch schedule:', data.error);
        throw new Error(data.error || 'Schedule not found');
      }
    } catch (error) {
      console.error('❌ Error fetching schedule:', error);
      throw error;
    }
  },

  /**
   * Create a new schedule
   * @param {Object} scheduleData - Schedule configuration
   * @returns {Promise<Object>} Created schedule
   */
  async createSchedule(scheduleData) {
    try {
      
      const userId = await getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const requestBody = {
        userId,
        ...scheduleData
      };
      
      try {
        const result = await post({
          apiName: 'userdata',
          path: '/schedules',
          options: {
            body: requestBody
          }
        }).response;
        
        const data = await result.body.json();
        
        if (data.success) {
          return data.schedule;
        } else {
          console.error('❌ Failed to create schedule:', data.error);
          console.error('❌ Validation errors:', data.errors);
          // Plan-limit errors use { reason, message, limit } rather than { error, errors }.
          const errorMessage = data.errors ?
            `Validation failed: ${JSON.stringify(data.errors)}` :
            (data.message || data.error || 'Failed to create schedule');
          throw new Error(errorMessage);
        }
      } catch (apiError) {
        // Structured 403 (§9, free-tier-cap-enforcement plan): distinct from a real failure —
        // surfaced as-is so the caller can open the pricing modal instead of an error toast.
        const waitlistBody = await parseWaitlistError(apiError);
        if (waitlistBody) {
          throw waitlistedError(waitlistBody);
        }
        const errorMessage = await extractErrorMessage(apiError, 'Failed to create schedule');
        console.error('❌ Error creating schedule:', errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error creating schedule:', error);
      throw error;
    }
  },

  /**
   * Update an existing schedule
   * @param {string} scheduleId - Schedule ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated schedule
   */
  async updateSchedule(scheduleId, updates) {
    try {
      
      const userId = await getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const result = await put({
        apiName: 'userdata',
        path: `/schedules/${scheduleId}`,
        options: {
          body: {
            userId,
            ...updates
          }
        }
      }).response;
      
      const data = await result.body.json();
      
      if (data.success) {
        return data.schedule;
      } else {
        console.error('❌ Failed to update schedule:', data.error);
        // Plan-limit errors use { reason, message, limit } rather than { error, errors }.
        throw new Error(data.message || data.error || 'Failed to update schedule');
      }
    } catch (apiError) {
      const waitlistBody = await parseWaitlistError(apiError);
      if (waitlistBody) {
        throw waitlistedError(waitlistBody);
      }
      const errorMessage = await extractErrorMessage(apiError, 'Failed to update schedule');
      console.error('❌ Error updating schedule:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Delete a schedule (soft delete)
   * @param {string} scheduleId - Schedule ID
   * @returns {Promise<void>}
   */
  async deleteSchedule(scheduleId) {
    try {
      
      const userId = await getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const result = await del({
        apiName: 'userdata',
        path: `/schedules/${scheduleId}?userId=${encodeURIComponent(userId)}`
      }).response;
      
      const data = await result.body.json();
      
      if (data.success) {
      } else {
        console.error('❌ Failed to delete schedule:', data.error);
        throw new Error(data.error || 'Failed to delete schedule');
      }
    } catch (apiError) {
      const waitlistBody = await parseWaitlistError(apiError);
      if (waitlistBody) {
        throw waitlistedError(waitlistBody);
      }
      const errorMessage = await extractErrorMessage(apiError, 'Failed to delete schedule');
      console.error('❌ Error deleting schedule:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Pause a schedule
   * @param {string} scheduleId - Schedule ID
   * @returns {Promise<Object>} Updated schedule
   */
  async pauseSchedule(scheduleId) {
    try {
      
      const userId = await getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const result = await post({
        apiName: 'userdata',
        path: `/schedules/${scheduleId}/pause`,
        options: {
          body: {
            userId,
            scheduleId
          }
        }
      }).response;
      
      const data = await result.body.json();
      
      if (data.success) {
        return data.schedule;
      } else {
        console.error('❌ Failed to pause schedule:', data.error);
        throw new Error(data.error || 'Failed to pause schedule');
      }
    } catch (apiError) {
      const waitlistBody = await parseWaitlistError(apiError);
      if (waitlistBody) {
        throw waitlistedError(waitlistBody);
      }
      const errorMessage = await extractErrorMessage(apiError, 'Failed to pause schedule');
      console.error('❌ Error pausing schedule:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Resume a paused schedule
   * @param {string} scheduleId - Schedule ID
   * @returns {Promise<Object>} Updated schedule
   */
  async resumeSchedule(scheduleId) {
    try {
      
      const userId = await getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const result = await post({
        apiName: 'userdata',
        path: `/schedules/${scheduleId}/resume`,
        options: {
          body: {
            userId,
            scheduleId
          }
        }
      }).response;
      
      const data = await result.body.json();
      
      if (data.success) {
        return data.schedule;
      } else {
        console.error('❌ Failed to resume schedule:', data.error);
        throw new Error(data.error || 'Failed to resume schedule');
      }
    } catch (apiError) {
      const waitlistBody = await parseWaitlistError(apiError);
      if (waitlistBody) {
        throw waitlistedError(waitlistBody);
      }
      const errorMessage = await extractErrorMessage(apiError, 'Failed to resume schedule');
      console.error('❌ Error resuming schedule:', errorMessage);
      throw new Error(errorMessage);
    }
  },

  /**
   * Get execution history for a schedule
   * @param {string} scheduleId - Schedule ID
   * @returns {Promise<Array>} Array of execution history entries
   */
  async getExecutionHistory(scheduleId) {
    try {
      
      const userId = await getUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const result = await get({
        apiName: 'userdata',
        path: `/schedules/${scheduleId}/history?userId=${encodeURIComponent(userId)}`
      }).response;
      
      const data = await result.body.json();
      
      if (data.success) {
        return data.history || [];
      } else {
        console.error('❌ Failed to fetch execution history:', data.error);
        throw new Error(data.error || 'Failed to fetch execution history');
      }
    } catch (error) {
      console.error('❌ Error fetching execution history:', error);
      throw error;
    }
  },

  /**
   * Format next run time with smart status detection
   * @param {string} isoString - ISO timestamp for next run
   * @param {Object} schedule - Full schedule object with lock info
   * @returns {string} Formatted date/time string
   */
  formatNextRunTime(isoString, schedule = null) {
    if (!isoString) return 'Not scheduled';
    
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = date - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    // If in the past
    if (diffMs < 0) {
      // Check if there's an active execution lock (job in progress)
      if (schedule?.lastExecutionLock) {
        const lockTime = new Date(schedule.lastExecutionLock).getTime();
        const lockAge = now.getTime() - lockTime;
        const lockAgeMinutes = Math.floor(lockAge / (1000 * 60));
        
        // If lock is less than 12 minutes old, job is likely still running
        if (lockAgeMinutes < 12) {
          return 'Generation in progress...';
        }
        
        // If lock is older than 12 minutes, it's likely stuck/failed
        // Check if the last execution failed
        if (schedule.lastExecutionResult === 'failed') {
          return 'Overdue (failed)';
        }
      }
      
      // Default overdue status
      return 'Overdue';
    }
    
    // If less than 24 hours away
    if (diffHours < 24) {
      if (diffHours === 0) {
        return `in ${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
      }
      return `in ${diffHours} hour${diffHours !== 1 ? 's' : ''} ${diffMins} min`;
    }
    
    // Otherwise show date and time in dd-Mmm-yyyy HH:mm format
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  }
};
